import { HighlightMatch, SearchItem } from '../types';
import { isModule } from '../../../helpers/windowLocation';
import { ASSET_SNIPPET, HighlightSegment, parseHighlightedText } from './highlightedText';

type Translate = (key: string) => string;

const TAG_REGEX = /<[^>]+>/gm;

// With the HTML tags removed and no bold emphasis.
const plainText = (text: string): HighlightSegment[] => {
	const stripped = text.replace(TAG_REGEX, '');

	return stripped ? [{ text: stripped, highlighted: false }] : [];
};

const label = (text: string): HighlightSegment => ({ text, highlighted: false });

/**
 * The line under a dropdown result item's name saying why it matched, as segments for `HighlightedText`. Only
 * transcript and document-text matches keep their emphasis. Empty when there's nothing to show.
 *
 * Field order matches the explainability badge's priority (HybridSearch/explainabilityBadge.ts), so
 * a row never names one source in its badge and a different one in its snippet.
 */
export const getMatchExplanation = (
	media: SearchItem,
	translate: Translate,
): HighlightSegment[] => {
	const match = media.highlight;

	if (!match) {
		return [];
	}

	if (match.copyright && match.copyright.length) {
		return plainText(`${translate('GeneralCopyright')}: ${match.copyright[0]}`);
	}

	if (match.tags && match.tags.length) {
		return plainText(`${translate('generalTags')}: ${match.tags[0]}`);
	}

	if (match.description && match.description.length) {
		return plainText(match.description[0]);
	}

	if (match.transcription) {
		return [
			label(`${translate('spotlight.transcript')} | "`),
			...parseHighlightedText(match.transcription[0], ASSET_SNIPPET),
			label('"'),
		];
	}

	if (match.fileContent) {
		return [
			label(`${translate('spotlight.text')} | "`),
			...parseHighlightedText(match.fileContent[0], ASSET_SNIPPET),
			label('"'),
		];
	}

	if (match.textFromImage) {
		return [label(translate('spotlight.textInImage'))];
	}

	if (media.description) {
		return plainText(media.description);
	}

	return [];
};

type RecentSearchesOptions = {
	searchTerm: string;
	canShowRecentSearches: boolean;
	addSearchTermToRecentSearches: (term: string) => void;
};

type ItemClickOptions = RecentSearchesOptions & {
	highlight: HighlightMatch | undefined;
};

export const openAssetResult = (id: string, options: RecentSearchesOptions) => {
	const { searchTerm, canShowRecentSearches, addSearchTermToRecentSearches } = options;

	window.bynder?.tools?.MediaModal?.open(id);

	if (!canShowRecentSearches) {
		return;
	}

	addSearchTermToRecentSearches(searchTerm);
};

export const onItemClick = (id: string, options: ItemClickOptions) => {
	const { highlight } = options;

	if (!id) {
		return;
	}

	if (isModule('workflow') || isModule('guidelines')) {
		window.location.replace(`/media/?mediaId=${id}`);

		return;
	}

	if (highlight?.transcription || highlight?.textFromImage) {
		const mediaModal = window.bynder?.tools?.MediaModal;
		Object.assign(mediaModal as Record<string, unknown>, {
			openAIModal: true,
			searchTranscriptMatch: highlight.transcription,
		});
	}

	openAssetResult(id, options);
};
